import {differenceInDays} from 'date-fns';
import {Flag, FlagProps} from 'platform/components';
import {Box, ImageProps, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil, omit} from 'ramda';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {CurrencyCodeType, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {useThumbnailImage} from '../../hooks/useThumbnailImage';
import {getFirstScrapedPrice} from '../PriceChangesPopper/utils/getFirstScrapedPrice';
import {VehicleThumbnail} from './components/VehicleThumbnail';

interface VehicleImageProps extends Omit<ImageProps, 'src'>, TestIdProps {
  vehicle?: SourcingVehicleDetailResponseBody;
  hasUnifiedSurfaces: boolean;
}

export function VehicleImage(props: VehicleImageProps) {
  const formatCurrency = useFormatCurrency();
  const image = useThumbnailImage(props.vehicle?.sourcingVehicle);
  const currency = props.vehicle?.sourcingVehicle?.price?.currency;
  const convertedPrice = props.vehicle?.summary.original;
  const convertedCurrencyCode = currency?.key as CurrencyCodeType;
  const firstScrapedPrice = getFirstScrapedPrice(props.vehicle?.sourcingVehicle, convertedPrice);
  const discount =
    isNotNil(convertedPrice) && isNotNil(firstScrapedPrice)
      ? firstScrapedPrice - convertedPrice
      : 0;
  const isSelectedInComparison =
    isNotNil(props.vehicle) && 'isSelectedInComparison' in props.vehicle
      ? props.vehicle.isSelectedInComparison
      : false;
  const isOriginalVehicle = props.vehicle?.sourcingVehicle?.adId.includes('_DUPLICATE');
  const isSold = props.vehicle?.sourcingVehicle?.isSold;
  const soldDaysAgo = isNotNil(props.vehicle?.sourcingVehicle.occurrence?.last)
    ? differenceInDays(new Date(), parseDate(props.vehicle.sourcingVehicle.occurrence.last))
    : null;
  const soldTagProps: Partial<FlagProps> =
    (soldDaysAgo ?? Number.POSITIVE_INFINITY) <= 30
      ? {colorScheme: 'neutral'}
      : {
          colorScheme: 'black',
          isSubtle: true,
        };
  const imageProps = omit(['vehicle', 'hasUnifiedSurfaces'], props);

  return (
    <Box position="relative" height="100%">
      {/* The height fills the stretched image column while it sits beside the content. Once the image is
          alone on its row the parent height is auto, so `ratio` governs instead. */}
      <VehicleThumbnail
        key={props.hasUnifiedSurfaces ? `${image.key}-${image.url}` : image.key}
        hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        src={image.url}
        alt={props.vehicle?.sourcingVehicle?.make?.translation}
        width="100%"
        height="100%"
        ratio="4/3"
        fit="cover"
        onError={image.onError}
        data-testid={suffixTestId('vehicleThumbnail', props)}
        {...imageProps}
      />
      <Box position="absolute" top={2} left={2}>
        <VStack spacing={2}>
          <Show when={isSelectedInComparison}>
            <Flag
              label={i18n.t('general.labels.selected')}
              colorScheme="blue"
              size="small"
              data-testid={suffixTestId('selected', props)}
            />
          </Show>
          <Show when={isOriginalVehicle}>
            <Flag
              label={i18n.t('entity.vehicle.labels.original')}
              colorScheme="blue"
              size="small"
              data-testid={suffixTestId('original', props)}
            />
          </Show>
          <Show when={isSold}>
            <Flag
              label={i18n.t('entity.vehicle.labels.soldNDaysAgo', {
                count: soldDaysAgo,
              })}
              {...soldTagProps}
              size="small"
              data-testid={suffixTestId('soldCar', props)}
            />
          </Show>
          <Show when={discount !== 0} whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}>
            <Flag
              label={
                discount >= 0
                  ? i18n.t('entity.vehicle.labels.discount', {
                      discount: formatCurrency(discount ?? 0, convertedCurrencyCode),
                    })
                  : i18n.t('entity.vehicle.labels.priceIncrease', {
                      increase: formatCurrency(-(discount ?? 0), convertedCurrencyCode),
                    })
              }
              colorScheme={discount >= 0 ? 'orange' : 'red'}
              size="small"
              data-testid={suffixTestId('discount', props)}
            />
          </Show>
        </VStack>
      </Box>
    </Box>
  );
}
